import React from "react";
import { Button, Tag, Collapse, Card, Tooltip, Progress } from "antd";
import LeaderboardPanel from "./LeaderboardPanel";

export default function InsightsPanel({
  isDark,
  showInsights,
  setShowInsights,
  insightFilter,
  tileFilterLabel,
  handleInsightTileClick,
  isTileActive,
  filteredDataCount,
  totalRecords,
  uniqueWards,
  wardNAPercent,
  alivePercent,
  marriedPercent,
  femalePercent,
  hasDirectorshipYes,
  mostIndigentWard,
  mostIndigentWardCount,
  mostIndigentWardPct,
  leastIndigentWard,
  leastIndigentWardCount,
  leastIndigentWardPct,
  topDeceased,
  VGI,
  nextBest,
  nextBestPctInWard,
  pdAlertPct,
  directorshipExposurePct,
  equityBalance,
  leaderboardProps,
}) {
  return (
    <div style={{ marginTop: 12, marginBottom: 16 }}>
      <Collapse
        className="insights-collapse"
        bordered={false}
        defaultActiveKey={[showInsights ? "insights" : ""]}
        onChange={(keys) => setShowInsights(keys.includes("insights"))}
      >
        <Collapse.Panel
          header={<span style={{ fontWeight: 700, color: isDark ? "#f5f5f5" : "#111" }}>Insights</span>}
          key="insights"
          showArrow
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "4px 0 10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Tag color={isDark ? "gold" : "gold"} style={{ color: "#111", fontWeight: 700 }}>
                TIP
              </Tag>
              <span style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                Click a tile to filter the table, cards, and charts. Click it again to clear.
              </span>
              {insightFilter && (
                <>
                  <Tag
                    color={isDark ? "#14141b" : "#f0f0f0"}
                    style={{ border: isDark ? "1px solid rgba(212,175,55,0.45)" : "1px solid #ddd", color: isDark ? "#f5f5f5" : "#111" }}
                  >
                    Active filter: <strong style={{ marginLeft: 6 }}>{tileFilterLabel()}</strong>
                  </Tag>
                  <Tag color={isDark ? "geekblue" : "geekblue"} style={{ color: isDark ? "#fff" : "#fff" }}>
                    {filteredDataCount} results
                  </Tag>
                </>
              )}
            </div>
            <div>
              <Button size="small" className="luxe-btn" onClick={() => handleInsightTileClick("CLEAR_ALL")} disabled={!insightFilter}>
                Clear tile filter
              </Button>
            </div>
          </div>

          <LeaderboardPanel isDark={isDark} {...leaderboardProps} />

          <div className="kpi-grid" style={{ marginTop: 4 }}>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Shows the overall dataset size in view after filters. All other percentages use this as the denominator.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Total Records = count of rows after filters.</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${isTileActive("CLEAR_ALL") ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("CLEAR_ALL")}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Total Records
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  {totalRecords}
                </div>
                {isTileActive("CLEAR_ALL") && <div className="active-ribbon">Active</div>}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Shows coverage breadth across wards to guide allocation and fairness.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Unique Wards = number of distinct ward numbers in view.</div>
                </div>
              }
            >
              <Card
                className="kpi-card"
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                }}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Unique Wards
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  {uniqueWards}
                </div>
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Missing ward info weakens planning and reporting. Lower is better.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Ward N/A% = records with no ward or "N/A" ÷ total records × 100.</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${isTileActive("WARD_NA") ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("WARD_NA")}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Ward N/A
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#d4af37" : "#d4af37" }}>
                  {wardNAPercent}%
                </div>
                <Progress percent={parseFloat(wardNAPercent)} showInfo={false} strokeColor="#d4af37" trailColor={isDark ? "#23232c" : "#eee"} />
                {isTileActive("WARD_NA") && <div className="active-ribbon">Active</div>}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Confirms active beneficiaries and improves service eligibility accuracy.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Alive% = records with status "Alive" ÷ total records × 100.</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${isTileActive("ALIVE") ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("ALIVE")}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Alive
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#d4af37" : "#d4af37" }}>
                  {alivePercent}%
                </div>
                <Progress percent={parseFloat(alivePercent)} showInfo={false} strokeColor="#d4af37" trailColor={isDark ? "#23232c" : "#eee"} />
                {isTileActive("ALIVE") && <div className="active-ribbon">Active</div>}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Household structure proxy for planning and case management.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Married% = records with marital status "MARRIED" ÷ total records × 100.</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${isTileActive("MARRIED") ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("MARRIED")}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Married
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#d4af37" : "#d4af37" }}>
                  {marriedPercent}%
                </div>
                <Progress percent={parseFloat(marriedPercent)} showInfo={false} strokeColor="#d4af37" trailColor={isDark ? "#23232c" : "#eee"} />
                {isTileActive("MARRIED") && <div className="active-ribbon">Active</div>}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Tracks gender mix for equitable outreach and reporting.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Female% = records with gender "Female" ÷ total records × 100.</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${isTileActive("FEMALE") ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("FEMALE")}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Female
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#d4af37" : "#d4af37" }}>
                  {femalePercent}%
                </div>
                <Progress percent={parseFloat(femalePercent)} showInfo={false} strokeColor="#d4af37" trailColor={isDark ? "#23232c" : "#eee"} />
                {isTileActive("FEMALE") && <div className="active-ribbon">Active</div>}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Flags potential conflicts of interest or eligibility risks that may require review.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Count = number of records inferred as "Yes". Progress = Count ÷ total records × 100.</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${isTileActive("DIRECTORSHIP") ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("DIRECTORSHIP")}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Has Directorship
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  {hasDirectorshipYes}
                </div>
                <Progress
                  percent={((hasDirectorshipYes / totalRecords) * 100).toFixed(1)}
                  showInfo={false}
                  strokeColor="#d4af37"
                  trailColor={isDark ? "#23232c" : "#eee"}
                />
                {isTileActive("DIRECTORSHIP") && <div className="active-ribbon">Active</div>}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Shows where indigent caseload is most concentrated to prioritise outreach and resources.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Count all records per ward and pick the highest. Percent = ward count ÷ total records × 100.</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${isTileActive("WARD_EQ", { ward: mostIndigentWard }) ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("WARD_EQ", { ward: mostIndigentWard })}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Most Indigents: Ward {mostIndigentWard}
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  {mostIndigentWardCount} {mostIndigentWardCount === 1 ? "record" : "records"}
                </div>
                <Progress
                  percent={parseFloat(mostIndigentWardPct.toFixed(1))}
                  showInfo={false}
                  strokeColor="#d4af37"
                  trailColor={isDark ? "#23232c" : "#eee"}
                />
                {isTileActive("WARD_EQ", { ward: mostIndigentWard }) && <div className="active-ribbon">Active</div>}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Identifies wards with the smallest caseloads where lighter-touch interventions may suffice.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Count all records per ward and pick the lowest. Percent = ward count ÷ total records × 100.</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${isTileActive("WARD_EQ", { ward: leastIndigentWard }) ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("WARD_EQ", { ward: leastIndigentWard })}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Least Indigents: Ward {leastIndigentWard}
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  {leastIndigentWardCount} {leastIndigentWardCount === 1 ? "record" : "records"}
                </div>
                <Progress
                  percent={parseFloat(leastIndigentWardPct.toFixed(1))}
                  showInfo={false}
                  strokeColor="#d4af37"
                  trailColor={isDark ? "#23232c" : "#eee"}
                />
                {isTileActive("WARD_EQ", { ward: leastIndigentWard }) && <div className="active-ribbon">Active</div>}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Highlights wards where deceased rate is highest so you can prioritise records clean-up.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Compute deceased% per ward = "Deceased" ÷ ward total × 100, then show the top ward.</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${topDeceased && topDeceased[0] && isTileActive("WARD_EQ", { ward: topDeceased[0].ward }) ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => (topDeceased && topDeceased[0] ? handleInsightTileClick("WARD_EQ", { ward: topDeceased[0].ward }) : null)}
              >
                {topDeceased && topDeceased.length > 0 ? (
                  <>
                    <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                      Deceased High: Ward {topDeceased[0].ward}
                    </div>
                    <div className="kpi-value" style={{ color: "#ff6666" }}>
                      {topDeceased[0].deceasedPercent.toFixed(1)}%
                    </div>
                    <Progress percent={parseFloat(topDeceased[0].deceasedPercent.toFixed(1))} showInfo={false} strokeColor="#ef4444" trailColor={isDark ? "#23232c" : "#eee"} />
                    {isTileActive("WARD_EQ", { ward: topDeceased[0].ward }) && <div className="active-ribbon">Active</div>}
                  </>
                ) : (
                  <>
                    <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                      Deceased High
                    </div>
                    <div className="kpi-value" style={{ color: "#ff6666" }}>
                      N/A
                    </div>
                  </>
                )}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Measures overall payoff for data quality by rewarding Matched and Alive records and penalising Ward N/A. Higher is better.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>VGI = 0.6×Matched% + 0.3×Alive% + 0.1×(100 − Ward N/A%).</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${isTileActive("CLEAR_ALL") ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("CLEAR_ALL")}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Verification Game Index
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#d4af37" : "#d4af37" }}>
                  {VGI}
                </div>
                <Progress percent={VGI} showInfo={false} strokeColor="#d4af37" trailColor={isDark ? "#23232c" : "#eee"} />
                {isTileActive("CLEAR_ALL") && <div className="active-ribbon">Active</div>}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Pick the single ward where verification effort yields the highest expected gain now.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Score = Unverified × (Unverified ÷ Total in ward). Unverified includes Not Matched or missing ID.</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${isTileActive("WARD_EQ", { ward: nextBest.ward }) ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("WARD_EQ", { ward: nextBest.ward })}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Next Best Move: Ward {nextBest.ward}
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  {nextBest.unverified}/{nextBest.total} unverified
                </div>
                <Progress percent={nextBestPctInWard} showInfo={false} strokeColor="#d4af37" trailColor={isDark ? "#23232c" : "#eee"} />
                {isTileActive("WARD_EQ", { ward: nextBest.ward }) && <div className="active-ribbon">Active</div>}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>High duplicate ID rates suggest short-term incentives to "defect" with duplicate records, causing long-term data costs.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>PD Alert = Duplicate IDs ÷ Total Records × 100.</div>
                </div>
              }
            >
              <Card
                className={`kpi-card ${isTileActive("DUPLICATES") ? "active" : ""}`}
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("DUPLICATES")}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  PD Alert: Duplicates
                </div>
                <div className="kpi-value" style={{ color: "#ff6666" }}>
                  {pdAlertPct}%
                </div>
                <Progress percent={pdAlertPct} showInfo={false} strokeColor="#ef4444" trailColor={isDark ? "#23232c" : "#eee"} />
                {isTileActive("DUPLICATES") && <div className="active-ribbon">Active</div>}
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Highlights where potential conflicts or eligibility risks might cluster for deeper review.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Directorship Exposure = Records with directorship ÷ Total Records × 100.</div>
                </div>
              }
            >
              <Card
                className="kpi-card"
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                  cursor: "pointer",
                }}
                onClick={() => handleInsightTileClick("DIRECTORSHIP")}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Directorship Exposure
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#d4af37" : "#d4af37" }}>
                  {directorshipExposurePct}%
                </div>
                <Progress percent={directorshipExposurePct} showInfo={false} strokeColor="#d4af37" trailColor={isDark ? "#23232c" : "#eee"} />
              </Card>
            </Tooltip>
            <Tooltip
              color={isDark ? "#14141b" : "#fff"}
              title={
                <div style={{ maxWidth: 320, color: isDark ? "#f5f5f5" : "#111" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Why</div>
                  <div>Encourages fair targeting across genders so no group is unintentionally underserved.</div>
                  <div style={{ fontWeight: 700, margin: "8px 0 4px" }}>How</div>
                  <div>Equity Balance = 100 − |Female% − 50| × 2. 100 is perfectly balanced.</div>
                </div>
              }
            >
              <Card
                className="kpi-card"
                bodyStyle={{ padding: 0 }}
                style={{
                  background: isDark ? "#0f1117" : "#fff",
                  color: isDark ? "#f5f5f5" : "#111",
                  border: isDark ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(0,0,0,0.08)",
                  boxShadow: isDark ? "0 6px 18px rgba(0,0,0,.28)" : "0 4px 14px rgba(0,0,0,.08)",
                }}
              >
                <div className="kpi-title" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  Equity Balance
                </div>
                <div className="kpi-value" style={{ color: isDark ? "#f5f5f5" : "#111" }}>
                  {equityBalance}
                </div>
                <Progress percent={equityBalance} showInfo={false} strokeColor="#d4af37" trailColor={isDark ? "#23232c" : "#eee"} />
              </Card>
            </Tooltip>
          </div>
        </Collapse.Panel>
      </Collapse>
    </div>
  );
}
